/**
 * 写作页面视图组件
 * 整合写作相关的功能和界面
 *
 * @view Writing
 * @uses TypewriterText
 * @uses BasicInfoForm
 * @uses TechnicalInfoForm
 */

<template>
  <div class="writing-container">
    <StepsBar :current-step="currentStep" @change="handleChangeStep" />

    <div class="main-content">
      <div :class="{'left-panel': true, 'left-panel-slider': voiceSliderOpen}" ref="guideStep1Ref">
        <CardTitle icon="data-source">信息输入</CardTitle>
        <div class="data-source" :style="currentStep === 3 ? {height: 'calc(100% - 250px)'} : {height: '100%'}">
          <div v-if="dataSourceTab === 'voice'" :class="{'data-source-slider': true, 'open': voiceSliderOpen}">
            <VoiceRecordingAnchor
              :list="voiceSliderList"
              @change-anchor="handleChangeVoiceAnchor"
            />
          </div>
          <el-icon
            v-if="dataSourceTab === 'voice'"
            :size="16"
            class="slider-trigger"
            @click="voiceSliderOpen = !voiceSliderOpen"
          >
            <svg-icon name="writing/slider" />
          </el-icon>
          <el-tabs v-model="dataSourceTab">
            <el-tab-pane label="语音输入" name="voice">
              <VoiceInputSection
                ref="voiceInputSectionRef"
                @voice-input="handleVoiceInput"
                @file-change="handleVoiceFileChange"
                :show-controls="currentStep === 0 && !isView"
                :projectId="projectId"
                :defaultValue="defaultFileData.voice"
              />
            </el-tab-pane>
            <el-tab-pane label="文档上传" name="fileupload">
              <FileUploadSection
                ref="uploadFileSectionRef"
                :formData="formData[`step${currentStep}`]"
                @update:image="handleChangeImage"
                @update:formData="handleFileExtract"
                @file-change="handleUploadFileChange"
                @generating-status="handleGeneratingStatus"
                @field-modified="handleFieldModified"
                :show-controls="currentStep === 0 && !isView"
                :show-upload-image="currentStep !== 3 && !isView"
                :projectId="projectId"
                :defaultValue="defaultFileData.file"
              />
            </el-tab-pane>
          </el-tabs>
        </div>
        <RecordForm ref="recordFormRef" v-model="recordList" :recordlist="recordList" v-if="currentStep === 3" />
      </div>
      <div class="main-panel" ref="guideStep2Ref">
        <el-scrollbar :height="mainHeight" ref="mainScrollbar">
          <div class="form-section" v-if="currentStep <= 2">
            <CardTitle icon="analysis" v-if="currentStep === 0">要点提取</CardTitle>
            <CardTitle icon="analysis" v-else-if="currentStep === 1">提纲内容</CardTitle>
            <CardTitle icon="analysis" v-else>初版内容</CardTitle>

            <BasicInfoForm
              v-model="formData[`step${currentStep}`]"
              :disabled="isView"
            />

            <TechnicalInfoForm
              ref="TechnicalInfoFormRef"
              v-model="formData[`step${currentStep}`]"
              :termInterpretation="termInterpretation"
              :reason-data="formReasonData[`step${currentStep}`]"
              :show-error="showError"
              :extracted-source-file="currentStep === 0 ? extractedSourceFile : []"
              :is-outline-step="currentStep === 2"
              :saved-form-data="savedFormData"
              :getStepParams="getStepParams"
              :should-reset="shouldReset"
              :isAuto="isAuto"
              :disabled="isView"
              :projectId="projectId"
              :generateHighlight="generateHighlight"
              :generatingFields="generatingFields"
              :reason-generating-fields="reasonGeneratingFields"
              :reason-expand="reasonExpand"
              :reason-time="reasonTime"
              :current-step="currentStep"
              @save-model="handleSaveModel"
              @field-modified="handleFieldModified"
              @scroll-to="handleScrollTo"
              @streaming-status="handleStreamingStatus"
              @generating-status="handleGeneratingStatus"
              @score-update="handleScoreUpdate"
              @history-update="handleCacheAI"
              @reason-update="handleReasonUpdate"
              @reason-status="handleReasonStatus"
            />
          </div>
          <div class="form-section preview-section" v-else>
            <CardTitle icon="file">确稿预览</CardTitle>
            <div class="preview-office-viewer" style="height: calc(100% - 40px); overflow-y: auto;">
            </div>
          </div>
        </el-scrollbar>
      </div>
      <div class="right-panel" ref="guideStep3Ref">
        <el-scrollbar :height="mainHeight">
          <CardTitle icon="analysis">
            智能分析
            <el-tooltip content="重新分析" placement="bottom" effect="light">
              <el-icon @click="handleReScore" style="cursor: pointer" v-if="getHasScore(scores) && !isView && (currentStep === 1 || currentStep === 2)"><Refresh /></el-icon>
            </el-tooltip>
          </CardTitle>
          <ScorePanel
            v-if="currentStep >= 1 && currentStep <= 3"
            :isView="isView"
            :scores="scores"
            :current-step="currentStep"
          />

          <ProgressTable
            v-if="currentStep >= 0 && currentStep <= 3"
            :progress-status="progressStatus"
            :currentStep="currentStep"
            :is-first-step="currentStep === 0"
            :has-background="!!formData[`step${currentStep}`]?.background?.trim()"
            :has-problem="!!formData[`step${currentStep}`]?.problem?.trim()"
            :has-technical="!!formData[`step${currentStep}`]?.technical?.trim()"
            :has-effect="!!formData[`step${currentStep}`]?.effect?.trim()"
            :has-implementation="!!formData[`step${currentStep}`]?.implementation?.trim()"
            :has-termInterpretation="!!formData[`step${currentStep}`]?.termInterpretation?.trim()"
            :has-description="!!formData[`step${currentStep}`]?.description?.trim()"
            :is-generating="isGenerating"
            :generating-fields="generatingFields"
          />
        </el-scrollbar>
      </div>
    </div>
    <div class="button-group" v-if="isView">
      <el-button size="large" v-if="currentStep > 0" @click="handlePrevStep">
        上一步
      </el-button>
      <el-button size="large" type="primary" class="generate-btn" v-if="currentStep < 3" @click="handleNextStep">
        下一步
      </el-button>
    </div>
    <div class="button-group" v-else>
      <el-button size="large" @click="handleAbandon">
        放弃
      </el-button>
      <el-button size="large" v-if="currentStep == 3" @click="handlePrevStep">
        上一步
      </el-button>
      <el-button
        v-if="currentStep === 2 && authPermission(['comparison'])"
        size="large"
        :disabled="isGenerateButtonDisabled"
        @click="handleUploadReport"
      >
        查新对比
      </el-button>
      <el-popover
        v-model:visible="previewMenuVisible"
        placement="top"
        :width="240"
        trigger="manual"
        popper-class="preview-menu-popover"
      >
        <div class="preview-menu">
          <el-button
            text
            @click="handleDirectPreview2"
            class="menu-button"
          >
            <template #icon>
              <el-icon><Document /></el-icon>
            </template>
            直接预览
          </el-button>
          <div class="divider"></div>
          <el-button
            text
            @click="handleTemplatePreview"
            class="menu-button"
          >
            <template #icon>
              <el-icon><Files /></el-icon>
            </template>
            选择模板
          </el-button>
        </div>
        <template #reference>
          <el-button
            type="primary"
            size="large"
            class="generate-btn"
            @click="handleGenerateOutline"
            :disabled="isGenerateButtonDisabled"
            :class="{ 'export-btn': currentStep === 3 }"
          >
            {{ buttonText }}
          </el-button>
        </template>
      </el-popover>
    </div>
    <TemplateSelectDialog
      v-model="templateDialogVisible"
      @template-selected="handleTemplateSelected"
    />

    <AnalysisResultDialog
      v-model:visible="analysisDialogVisible"
      :projectId="projectId"
      :form-data="formData[`step${currentStep}`]"
      @close="analysisDialogVisible = false"
      @export="handleExportAnalysis"
      @submit="handleSaveAnalysisForm"
    />
    <DraftAutoDialog
      ref="draftAutoDialogRef"
      @ok="handleDraftOk"
    />
    <PrevStepDialog
      ref="prevStepDialogRef"
    />
    <el-tour
      v-model="guideVisible"
      class="guide-tour"
      :show-close="false"
      :target-area-clickable="false"
    >
      <el-tour-step :target="guideStep1Ref" :next-button-props="{ children: '下一步' }" title="数据源" placement="right">
        <div>语音录入或选择文件上传，ai智能识别内容 并分析提取要点</div>
      </el-tour-step>
      <el-tour-step :target="guideStep2Ref" :next-button-props="{ children: '下一步' }" :prev-button-props="{ children: '上一步 '}" title="信息录入" placement="right">
        <div>根据数据源中提取的相关要点，进行校验修改及补充完善。</div>
      </el-tour-step>
      <el-tour-step :target="guideStep3Ref" :next-button-props="{ children: '完成' }" :prev-button-props="{ children: '上一步 '}" title="分析结果" placement="right">
        <div>对前置信息进行分析评估，并标记各个部分的状态</div>
      </el-tour-step>
      <template #indicators="{ current, total }">
        <span>{{ current + 1 }}/{{ total }}</span>
        <a @click="() => handleShowGuide(false)" v-if="current + 1 < total">跳过</a>
      </template>
    </el-tour>
  </div>
  <div style="position: fixed; bottom: 10px; right: 0px;width: 300px;">
      <p style="font-size: 14px; color: #999999;text-align: left;">生成式人工智能服务备案号：</p>
      <p style="font-size: 14px; color: #999999;text-align: left;">Beijing-XinZhiShulPT-202510200145</p>
      </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, onBeforeMount, unref } from 'vue'
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus'
import { Close, Upload, Document, Download, Files, Picture, Refresh } from '@element-plus/icons-vue'
import VuePdfEmbed from 'vue-pdf-embed'

import StepsBar from '@/components/StepsBar.vue'
import CardTitle from '@/components/CardTitle.vue'
import BasicInfoForm from '@/components/form/BasicInfoForm.vue'
import TechnicalInfoForm from '@/components/form/TechnicalInfoForm.vue'
import FileUploadSection from '@/components/form/FileUploadSection.vue'
import VoiceInputSection from '@/components/form/VoiceInputSection.vue'
import VoiceRecordingAnchor from '@/components/form/VoiceRecordingAnchor.vue'
import ScorePanel from '@/components/score/ScorePanel.vue'
import ProgressTable from '@/components/score/ProgressTable.vue'
import AnalysisResultDialog from '@/components/dialogs/AnalysisResultDialog.vue'
import TemplateSelectDialog from '@/components/dialogs/TemplateSelectDialog.vue'
import DraftAutoDialog from './DraftAutoDialog'
import PrevStepDialog from './PrevStepDialog'
import RecordForm from '@/components/form/recordForm.vue'
import { fetchSaveStep1, fetchSaveStep2, fetchSaveStep3, fetchSaveStep4, fetchSaveStep1Cache, fetchSaveStep2Cache, fetchSaveStep3Cache, fetchSaveStepModel } from '@/api/writing'
import { fetchInfo, fetchCreate,fetchRecordList,fetchRecordAdd,fetchWordUpload,fetchProjectFiles } from '@/api/project'
import { guideEmitter } from '@/utils/event'
import { authPermission } from '@/utils/permission'
import { useRoute, useRouter } from 'vue-router'
import { fetchUpload } from '../../api/project'
import {OfficeExporter} from "@/utils/officeViewer";

const cacheIntervalTime = process.env.VUE_APP_WRITING_INTERVAL_TIME

const route = useRoute();
const router = useRouter();
// 项目id
const projectId = ref(route.query.id)
const mainHeight = ref(0);
const currentStep = ref(0)
const writingStepTitles = ['内容录入', '要点提纲', '初版生成', '确稿留存']
const updateWritingPageTitle = (step = currentStep.value) => {
  document.title = `交底书完善-${writingStepTitles[step] || writingStepTitles[0]}`
}
watch(currentStep, (step) => {
  updateWritingPageTitle(step)
}, { immediate: true })
const showError = ref(false)
const analysisDialogVisible = ref(false)
const guideVisible = ref(false)
const guideStep1Ref = ref()
const guideStep2Ref = ref()
const guideStep3Ref = ref()
const draftAutoDialogRef = ref()
const prevStepDialogRef = ref()
const TechnicalInfoFormRef = ref()
const recordFormRef = ref()
const isGenerating = ref(false)
const generatingFields = ref({
  background: false,
  problem: false,
  technical: false,
  effect: false,
  implementation: false,
  termInterpretation:false,
  description: false
})
const termInterpretation = ref('')
const cacheInterval = ref()
const isView = ref(false)
const viewScoreData = ref()
const defaultFileData = ref({ voice: [], file: [] })
// 数据源tab
const dataSourceTab = ref('voice')
// 语音输入左侧滑块
const voiceSliderOpen = ref(false)
// 语音输入左侧列表
const voiceSliderList = ref([])
// 语音输入ref
const voiceInputSectionRef = ref()
const uploadFileSectionRef = ref()

const voiceCacheList = ref([])
const uploadCacheList = ref([])

const formReasonData = ref({
  step0: {},
  step1: {},
  step2: {}
})

const reasonGeneratingFields = ref({
  background: false,
  problem: false,
  technical: false,
  effect: false,
  implementation: false,
  termInterpretation:false,
  description: false
})

const reasonExpand = ref({
  background: false,
  problem: false,
  technical: false,
  effect: false,
  implementation: false,
  termInterpretation:false,
  description: false
})

const reasonTimeStart = ref({
  background: 0,
  problem: 0,
  technical: 0,
  effect: 0,
  implementation: 0,
  termInterpretation:0,
  description: 0
})

const reasonTime = ref({
  background: 0,
  problem: 0,
  technical: 0,
  effect: 0,
  implementation: 0,
  termInterpretation:0,
  description: 0
})

const formData = ref({
  step0: {
    title: '',
    inventor: '',
    field: '',
    project: '',
    background: '',
    problem: '',
    technical: '',
    effect: '',
    implementation: '',
    description: '',
    imageUrls: []
  },
  step1: {
    title: '',
    inventor: '',
    field: '',
    project: '',
    background: '',
    problem: '',
    technical: '',
    effect: '',
    implementation: '',
    description: '',
    imageUrls: []
  },
  step2: {
    title: '',
    inventor: '',
    field: '',
    project: '',
    background: '',
    problem: '',
    technical: '',
    effect: '',
    implementation: '',
    termInterpretation: '',
    description: '',
    imageUrls: []
  },
  step3: {
    title: '',
    inventor: '',
    field: '',
    project: '',
    background: '',
    problem: '',
    technical: '',
    effect: '',
    implementation: '',
    termInterpretation: '',
    description: '',
    imageUrls: []
  }
})

// 添加提取来源件名以及数据
const extractedSourceFile = ref([])

const scores = ref({
  overall: {
    dimensions: [],
    scores: [],
    comments: ''
  },
  completeness: {
    dimensions: [],
    scores: [],
    comments: ''
  },
  innovation: {
    dimensions: [],
    scores: [],
    comments: ''
  },
  novelty: {
    dimensions: [],
    scores: [],
    comments: ''
  }
})

const progressStatus = ref({
  background: false,
  problem: false,
  technical: false,
  effect: false,
  implementation: false,
  description: false
})
// 是不是一键生成
const isAuto = ref(false)
// 生成时的高亮
const generateHighlight = ref({
  background: [],
  problem: [],
  technical: [],
  effect: [],
  implementation: [],
  description: []
})

const buttonText = computed(() => {
  switch(currentStep.value) {
    case 0: return '生成提纲'
    case 1: return '初版生成'
    case 2: return '确稿预览'
    case 3: return '导出文档'
    default: return '导出文档'
  }
})

// 添加一个新的计算属性来判断按钮是否应该禁用
const isGenerateButtonDisabled = computed(() => {
  // 如果正在生成中或者有字段正在流式打印，则禁用按钮
  const result = isGenerating.value || Object.values(generatingFields.value).some(v => v)
  if (currentStep.value === 2 && !isAuto.value) {
    return result || !TechnicalInfoFormRef.value?.isComplete()
  }
  return result
})

// 添加新的响应式变量
const previewMenuVisible = ref(false)
const officeViewerUrl = ref('')
const templateDialogVisible = ref(false)
const mainScrollbar = ref(null)
const savedFormData = ref(null)

// 添加请求控制器
const abortController = ref(null)
const scoreAbortController = ref(null)
const isAborted = ref(false)

watch(() => dataSourceTab.value, () => {
  voiceSliderOpen.value = false
})

watch(currentStep,  (val) => {
  if (val === 3) {
     getRecordList(projectId.value)
     handleDirectPreview3();
  }
}, { immediate: true })
const recordList = ref([])
const getRecordList = async (id) => {
  console.log(1)
  try {
    const result = await fetchRecordList(id)
    console.log('result', result)
    recordList.value = result || []

  } catch (error) {
    console.error('获取记录列表失败:', error)
  }
}

const getStepParams = () => {
  const step1 = formData.value[`step0`] || {}
  const step2 = formData.value[`step1`] || {}
  return {
    step1: {
      solution: step1.technical,
      effect: step1.effect,
      implementation: step1.implementation,
      background: step1.background,
      problem: step1.problem,
      description: step1.description,
    },
    step2: {
      solution: step2.technical,
      effect: step2.effect,
      implementation: step2.implementation,
      background: step2.background,
      problem: step2.problem,
      description: step2.description,
    }
  }
}

const handlePrevStep = () => {
  currentStep.value --
  if (viewScoreData.value?.[`step${currentStep.value}`]) {
    scores.value = viewScoreData.value[`step${currentStep.value}`]
  }
}



const handleNextStep = () => {
  currentStep.value ++
  if (viewScoreData.value?.[`step${currentStep.value}`]) {
    scores.value = viewScoreData.value[`step${currentStep.value}`]
  }
}
/**
 * 处理流式响应并更新表单数据
 * @param {Response} response - fetch响应对象
 * @param {string} field - 要更新的字段名
 * @param {number} nextStep - 下一步的索引
 */
const handleStreamResponse = async (response, field, nextStep, fieldIndex) => {
  const reader = response.body.getReader()
  let accumulatedContent = ''
  let reasoningContent = ''

  if (currentStep.value === 2) {
    isGenerating.value = true
  }
  generatingFields.value[field] = true

  try {
    while (true) {
      // 如果已经被取消，停止处理流
      if (isAborted.value) {
        reader.cancel()
        break
      }

      const { done, value } = await reader.read()

      if (done) {
        generatingFields.value[field] = false
        handleReasonStatus({ field, status: false })
        // 检查是否所有字段都完成了
        if (currentStep.value === 2) {
          isGenerating.value = Object.values(generatingFields.value).some(v => v)
        }
        // 如果生成的内容为空，重置该字段的表单数据
        if (!accumulatedContent.trim()) {
          if (field === 'imageUrls') {
            formData.value[`step${nextStep}`][field][fieldIndex].content = ''
          } else {
            formData.value[`step${nextStep}`][field] = ''
          }
        }
        break
      }

      const chunk = new TextDecoder().decode(value)
      const lines = chunk.split('\n')

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(5).trim()
          if (jsonStr === '[DONE]') continue
          try {
            const data = JSON.parse(jsonStr)
            if (data.result) {
              accumulatedContent += data.result
              await nextTick(() => {
                if (field === 'imageUrls') {
                  formData.value[`step${nextStep}`][field][fieldIndex].content = accumulatedContent
                } else {
                  formData.value[`step${nextStep}`][field] = accumulatedContent
                }
                if (data.highlight && field !== 'imageUrls') {
                  generateHighlight.value[field] = generateHighlight.value[field] || []
                  generateHighlight.value[field].push({ text: data.result, color: data.color })
                }
                handleReasonStatus({ field, status: false })
              })
            } else if (data.reasoning && field !== 'imageUrls') {
              reasoningContent += data.reasoning
              formReasonData.value[`step${nextStep}`][field] = reasoningContent
              handleReasonStatus({ field, status: true })
            }
          } catch (e) {
            console.warn('解析 SSE 数据失败:', e)
          }
        }
      }
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('流式响应处理被取消')
      return
    }
    throw error
  } finally {
    generatingFields.value[field] = false
    if (currentStep.value === 2) {
      isGenerating.value = Object.values(generatingFields.value).some(v => v)
    }
  }
}

const handleDraftOk = async (auto, saveFlag = true) => {
  console.log('handleDraftOk',)
  // 如果当前有评分请求在进行中，取消它
  if (scoreAbortController.value) {
    scoreAbortController.value.abort()
    scoreAbortController.value = null
  }

  isAuto.value = auto
  // 获取当前步骤的表单数据
  const stepData = formData.value[`step${currentStep.value}`]
  const lastStepData = formData.value[`step${currentStep.value - 1}`]
  const nextStep = currentStep.value + 1

  if (saveFlag) {
    fetchSaveStep2(projectId.value, getSaveData({ isAuto: isAuto.value }))
  }

  isAborted.value = false
  // 创建新的 AbortController
  abortController.value = new AbortController()
  // 重置评分面板状态
  scores.value = {
    overall: {
      dimensions: [],
      scores: [],
      comments: ''
    },
    completeness: {
      dimensions: [],
      scores: [],
      comments: ''
    },
    innovation: {
      dimensions: [],
      scores: [],
      comments: ''
    },
    novelty: {
      dimensions: [],
      scores: [],
      comments: ''
    }
  }

  const loadingText = '正在生成初稿...';
  let loading = null

  loading = ElLoading.service({
    lock: true,
    text: loadingText,
    background: 'rgba(255, 255, 255, 0.7)'
  })

  try {
    // 保存当前表单数据，用于后续接口调用
    savedFormData.value = { ...stepData }

    // 初始化下一步的表单数据，除了基本信息和图片外都清空
    formData.value[`step${nextStep}`] = {
      title: stepData.title || '',
      inventor: stepData.inventor || '',
      field: stepData.field || '',
      project: stepData.project || '',
      background: '',
      problem: '',
      technical: '',
      effect: '',
      implementation: '',
      description: '',
      imageUrls: stepData.imageUrls?.map(v => ({ ...v, content: '' })) || []
    }

    // 先切换到下一步，这样可以立即看到流式内容
    currentStep.value = nextStep
    await nextTick()

    // 滚动到顶部
    if (mainScrollbar.value) {
      mainScrollbar.value.setScrollTop(0)
    }

    // 设置正在生成的字段，这样会隐藏"确认并继续撰写"按钮
    generatingFields.value.technical = true

    const response = await fetch('/api/draft/solution', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream'
      },
      body: JSON.stringify({
        solution: savedFormData.value.technical,
        ...getStepParams()
      }),
      signal: abortController.value?.signal
    })

    if (!response.ok) {
      throw new Error('技术方案生成请求失败')
    }

    // 关闭 loading
    loading.close()

    // 使用 handleStreamResponse 处理流式响应
    await handleStreamResponse(response, 'technical', nextStep)

    if (auto) {
      if (abortController.value?.signal.aborted) return
      await TechnicalInfoFormRef.value?.handleConfirmAndContinue('technical')

      if (abortController.value?.signal.aborted) return
      await TechnicalInfoFormRef.value?.handleConfirmAndContinue('effect')

      if (abortController.value?.signal.aborted) return
      await TechnicalInfoFormRef.value?.handleConfirmAndContinue('implementation')

      if (abortController.value?.signal.aborted) return

      ElMessage.success('初版生成成功')
      console.log('formData', formData.value)
      termInterpretation.value = formData.value[`step${currentStep.value}`].termInterpretation
      handleSaveModel()
    }
    fetchSaveStep3Cache(projectId.value, getSaveData({ cache: true }))
    // 保存当前表单数据，用于后续接口调用
    savedFormData.value = { ...stepData }
    fetchWordUpload({
      projectId: projectId.value,
      value: JSON.stringify(savedFormData.value),
      fileName: '初版生成-模型生成结果.docx'
    })
    return
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('请求被取消')
      return
    }
    console.error('技术方案生成失败:', error)
    // 确保在出错时也重置生成状态
    generatingFields.value.technical = false
    throw error
  }
}

// 规则校验
const handleValidate = () => {
  return new Promise(async (resolve) => {
    const stepData = formData.value[`step${currentStep.value}`]
    const params = {
      effect: stepData.effect,
      solution: stepData.technical,
      implementation: stepData.implementation,
      description: stepData.description,
      background: stepData.background,
      problem: stepData.problem,
      rules: '',
    }

    const loading = ElLoading.service({
      text: '正在校验...',
      lock: true,
      background: 'rgba(255, 255, 255, 0.7)'
    })

    const response = await fetch('/api/contentCheck/check', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(params),
    })
    if (!response.ok) {
      throw new Error('规则校验请求失败')
    }

    const data = await response.json()
    loading.close()

    if (!data.result) {
      ElMessage({
        message: data.errorText || '规则校验失败',
        type: 'error',
        center: true,
        duration: 8000,
        showClose: true,
        onClose: () => {
        },
        offset: 30,
        customClass: 'custom-message',
        round: true
    })
    }

    resolve(data.result);
  })
}
/**
 * 处理生成提纲按钮点击
 * 根据当前步骤执行不同的操作
 */
const handleGenerateOutline = async (saveFlag = true) => {
  console.log(`currentStep.value: ${currentStep.value}`)
  if (!validateCurrentStep()) {
    return
  }

  if (currentStep.value === 0 || currentStep.value === 1) {
    const validate = await handleValidate();
    if (!validate) return;
  }

  if (currentStep.value === 1) {
    draftAutoDialogRef.value.open();
    return;
  }

  // 如果当前有评分请求在进行中，取消它
  if (scoreAbortController.value) {
    scoreAbortController.value.abort()
    scoreAbortController.value = null
  }

  if (currentStep.value === 3) {
    await handleExportDocument()
    return
  }

  // 如果是从第二步到第三步(确稿预览),显示弹出菜单
  // if (currentStep.value === 2) {
  //   previewMenuVisible.value = true
  //   return
  // }
  // 确稿预览改成直接预览
  if (currentStep.value === 2) {
    handleDirectPreview2()
    return
  }

  let loading = null
  let hasAnySuccess = false
  let completedApiCalls = 0
  const totalApiCalls = 6 // 总共需要调用的API数量

  try {
    isGenerating.value = true
    isAborted.value = false
    // 创建新的 AbortController
    abortController.value = new AbortController()

    // 重置评分面板状态
    scores.value = {
      overall: {
        dimensions: [],
        scores: [],
        comments: ''
      },
      completeness: {
        dimensions: [],
        scores: [],
        comments: ''
      },
      innovation: {
        dimensions: [],
        scores: [],
        comments: ''
      },
      novelty: {
        dimensions: [],
        scores: [],
        comments: ''
      }
    }

    generateHighlight.value = {}

    const loadingText = currentStep.value === 0 ? '正在生成提纲...' :
        currentStep.value === 1 ? '正在生成初稿...' :
            '正在预览文档...'

    loading = ElLoading.service({
      lock: true,
      text: loadingText,
      background: 'rgba(255, 255, 255, 0.7)'
    })

    // 获取当前步骤的表单数据
    const stepData = formData.value[`step${currentStep.value}`]
    const nextStep = currentStep.value + 1

    // 定义所有需要调用的接口
    const apiGroups = [
      // 第一组：背景技术和技术问题
      [
        {
          url: '/api/outline/background',
          field: 'background',
          body: { background: stepData.background }
        },
        {
          url: '/api/outline/problem',
          field: 'problem',
          body: { problem: stepData.problem }
        }
      ],
      // 第二组：技术方案和效果
      [
        {
          url: '/api/outline/solution',
          field: 'technical',
          body: { solution: stepData.technical }
        },
        {
          url: '/api/outline/effect',
          field: 'effect',
          body: {
            solution: stepData.technical,
            effect: stepData.effect,
            background: stepData.background,
            problem: stepData.problem
          }
        }
      ],
      // 第三组：实施方式和附图说明
      [
        {
          url: '/api/outline/implementation',
          field: 'implementation',
          body: {
            solution: stepData.technical,
            effect: stepData.effect,
            implementation: stepData.implementation,
            background: stepData.background,
            problem: stepData.problem
          }
        },
        {
          url: '/api/outline/drawings',
          field: 'description',
          body: {
            description: stepData.description,
            solution: stepData.technical,
            effect: stepData.effect
          }
        },
        ...(stepData.imageUrls.map((image, index) => ({
          url: '/api/outline/drawings',
          field: 'imageUrls',
          fieldIndex: index,
          body: {
            description: image.content,
            solution: stepData.technical,
            effect: stepData.effect
          }
        })))
      ]
    ]

    // 异步调用 get-prompt 接口，不等待其完成
    fetch('/api/writing/get-prompt', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        background: stepData.background,
        problem: stepData.problem,
        technical: stepData.technical
      })
    }).catch(error => {
      console.error('获取提示词失败:', error)
    })

    // 调用内容录入的保存接口
    saveFlag && fetchSaveStep1(projectId.value, getSaveData())

    // 按组依次调用API
    for (const [groupIndex, group] of apiGroups.entries()) {
      // 如果已经被取消，停止后续请求
      if (isAborted.value) {
        break
      }

      try {
        const groupPromises = group.map(async ({ url, field, body, fieldIndex }) => {
          try {
            const response = await fetch(url, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/event-stream'
              },
              body: JSON.stringify(body),
              signal: abortController.value?.signal // 添加 signal
            })

            if (!response.ok) {
              throw new Error(`${field} 接口调用失败`)
            }

            hasAnySuccess = true

            // 如果是第一组的任意一个接口成功，就关闭loading并切换到下一步
            if (groupIndex === 0 && loading) {
              loading.close()
              loading = null

              // 初始化下一步的表单数据
              formData.value[`step${nextStep}`] = {
                ...stepData,
                background: '',
                problem: '',
                technical: '',
                effect: '',
                implementation: '',
                description: '',
                imageUrls: stepData.imageUrls?.map(v => ({ ...v, content: '' })) || []
              }
              // 切换到下一步
              currentStep.value = nextStep
              isAuto.value = false

              // 滚动到顶部
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              })
            }

            // 处理流式响应
            await handleStreamResponse(response, field, nextStep, fieldIndex)
              .then(() => {
                completedApiCalls++
                // 当所有API调用完成时，调用评分
                if (completedApiCalls === totalApiCalls && !isAborted.value) {
                  fetchScores(formData.value[`step${nextStep}`])
                }
              })
              .catch(error => {
                if (error.name === 'AbortError') {
                  console.log('流式响应被取消')
                  return
                }
                console.error(`处理 ${field} 流式响应失败:`, error)
              })

            return true
          } catch (error) {
            if (error.name === 'AbortError') {
              console.log('请求被取消')
              return false
            }
            console.error(`${field} 接口调用失败:`, error)
            completedApiCalls++
            return false
          }
        })

        // 等待当前组的所有API调用完成
        await Promise.all(groupPromises)
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('API组调用被取消')
          continue
        }
        console.error('API组调用失败:', error)
      }
    }

    if (hasAnySuccess && !isAborted.value) {

      // 如果loading还没关闭（说明第一组API都失败了），这里关闭它
      if (loading) {
        loading.close()
      }

      const successText = currentStep.value === 1 ? '提纲生成成功' :
          currentStep.value === 2 ? '初稿生成成功' :
              '文档生成成功'
      ElMessage.success(successText)
      // 生成成功缓存
      if (currentStep.value === 1) {
        fetchSaveStep2Cache(projectId.value, getSaveData({ cache: true }))
        // 保存当前表单数据，用于后续接口调用
        savedFormData.value = { ...stepData }
        fetchWordUpload({
          projectId: projectId.value,
          value: JSON.stringify(savedFormData.value),
          fileName: '要点提纲-模型生成结果.docx'
        })
      }
    } else if (!isAborted.value) {
      throw new Error('所有接口调用失败')
    }

  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('生成过程被取消')
      return
    }
    console.error('生成失败:', error)
    ElMessage.error(error.message || '生成失败，请重试')
    if (loading) {
      loading.close()
    }
  } finally {
    isGenerating.value = false
    handleSaveModel()
    // 清理 AbortController
    if (isAborted.value) {
      abortController.value = null
      isAborted.value = false
    }
  }
}

const validateCurrentStep = () => {
  const currentData = formData.value[`step${currentStep.value}`]
  showError.value = false
  console.log('currentData', currentData)
  const requiredTextareas = ['background', 'problem', 'technical']
  for (const field of requiredTextareas) {
    if (!currentData[field]?.trim()) {  // 添加 trim() 去除空格
      showError.value = true
      ElMessage.warning(`请写${getFieldLabel(field)}`)
      return false
    }
  }

  return true
}

const getFieldLabel = (field) => {
  const labels = {
    title: '发明名称',
    inventor: '发明人',
    field: '技术领域',
    project: '项目归属',
    background: '背景技术',
    problem: '技术问题',
    technical: '技术方案',
    effect: '有益效果',
    implementation: '具体实施方式',
    description: '附图说明'
  }
  return labels[field] || field
}

// 添加重置控制变量
const shouldReset = ref(false)

const handleAbandon = () => {
  ElMessageBox.confirm(
    '确定要放弃当前编辑吗？所有内容将被清空',
    '提示',
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning',
    }
  )
    .then(() => {
      // 取消所有正在进行的请求
      if (abortController.value) {
        isAborted.value = true
        abortController.value.abort()
      }

      // 如果有正在进行的评分请求，取消它
      if (scoreAbortController.value) {
        scoreAbortController.value.abort()
      }

      if (TechnicalInfoFormRef.value) {
        TechnicalInfoFormRef.value.stopScore()
      }

      const emptyData = {
        title: '',
        inventor: '',
        field: '',
        project: '',
        background: '',
        problem: '',
        technical: '',
        effect: '',
        implementation: '',
        description: '',
        imageUrls: []
      }

      formData.value = {
        step0: { ...emptyData },
        step1: { ...emptyData },
        step2: { ...emptyData },
        step3: { ...emptyData }
      }

      // 重置所有状态
      currentStep.value = 0
      extractedSourceFile.value = []
      isGenerating.value = false
      shouldReset.value = true
      isAuto.value = false
      defaultFileData.value={voice: [], file: [], image: []}
      reasonGeneratingFields.value = {
        background: false,
        problem: false,
        technical: false,
        effect: false,
        implementation: false,
        description: false
      }
      prevStepDialogRef.value?.close();

      // 使用 nextTick 确保状态更新后再重置 shouldReset
      nextTick(() => {
        shouldReset.value = false
      })

      localStorage.removeItem('formData')
      localStorage.removeItem('currentStep')

      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })

      fetchCreate().then((res) => {
        if (res) {
          router.replace(`/writing?id=${res.id}`);
        }
      });

    })
    .catch(() => {
      // 取消操作
    })
}

const handleUploadReport = () => {
  analysisDialogVisible.value = true
}

const handleExportAnalysis = () => {
  ElMessage.success('分析报告已导出')
}

const handleSaveAnalysisForm = (data) => {
  console.log('handleSaveAnalysisForm', data)
  // 确保传递当前步骤的完整数据，包imageUrls
  const currentStepData = formData.value[`step${currentStep.value}`]
  formData.value[`step${currentStep.value}`] = {
    ...data,
    imageUrls: currentStepData.imageUrls || []
  }
}

const handleExportDocument = async () => {
  const loading = ElLoading.service({
    lock: true,
    text: '正在导出文档...',
    background: 'rgba(255, 255, 255, 0.7)'
  })

  try {
    // 获取当前步骤的表单数据
    const currentData = formData.value[`step${currentStep.value}`]

    !isView.value && fetchSaveStep4(projectId.value, getSaveData())
    const result = await fetchRecordList(projectId.value)
    result.records.forEach((item,index) => {
      item.index = index+1
    })
    const exportData = {...currentData, recordLog:result}
    const download = `最终导出文件.zip`

    //调用导出接口
    const response = await fetch('/api/export', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(exportData)
    })
    if (!response.ok) {
      throw new Error('导出失败')
    }

    //获取文件blob
    const blob = await response.blob()

    if (!isView.value) {
      const file = new File([blob], `${currentData.title || '技术交底书'}.docx`, {type: blob.type});

      const uploadFile = new FormData()
      uploadFile.append('files', file)
      uploadFile.append('fileType', 2)
      uploadFile.append('projectId', projectId.value)

      await fetchUpload(uploadFile)
    }
    promiseDownload(fetchProjectFiles(projectId.value), download)


    // // 创建下载链接
    // const url = window.URL.createObjectURL(blob)
    // const link = document.createElement('a')
    // link.href = url
    // link.download = `${currentData.title || '技术交底书'}.docx`
    // document.body.appendChild(link)
    // link.click()
    // document.body.removeChild(link)
    // window.URL.revokeObjectURL(url)

    ElMessage.success('文档导出成功')
    await fetchRecordAdd({
      projectId: projectId.value,
        opType: "EXPORT",
        stepName: '确稿留存',
        stepCode: 'step3',
        blockCode: '',
        blockName: '',
        source: "user",
        description: "文件导出了",
        opDetail: '',
      })
    await getRecordList(projectId.value)
    await handleDirectPreview3()
  } catch (error) {
    console.error('导出失败:', error)
    ElMessage.error('导出失败，请重试')
  } finally {
    // 确保在任何情况下都关闭 loading
    loading.close()
  }
}

const promiseDownload = (promise, download) => {
  const loading = ElLoading.service({
    lock: true,
    text: '正在下载...',
    background: 'rgba(255, 255, 255, 0.7)'
  })

  promise
    .then(blob => {
      if (blob) {
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        ElMessage.success('下载成功')
        loading.close()
      } else {
        loading.close()
      }
    })
    .catch(() => {
      loading.close()
    })
}

// 追加或者覆盖模式的formData数据更新
const handleUpdateFormData = (extractedData) => {
  const { append } = extractedData;
  const currentStepData = formData.value[`step${currentStep.value}`];
  const textFields = ['background', 'problem', 'technical', 'effect', 'implementation', 'description']

  textFields.forEach(field => {
    if (append) {
      currentStepData[field] = (currentStepData[field] || '') + (extractedData[field] || '')
    } else {
      currentStepData[field] = (extractedData[field] || '')
    }
  })

  // 更新表单数据
  formData.value[`step${currentStep.value}`] = { ...currentStepData }

  // 更新进度状态
  // textFields.forEach(key => {
  //   if (progressStatus.value.hasOwnProperty(key)) {
  //     progressStatus.value[key] = (append ? progressStatus.value[key] : false) || Boolean(currentStepData[key])
  //   }
  // })

  // 更新提取来源文件名
  if (extractedData.filename) {
    // 检查是否已存在相同的文件
    const existingIndex = extractedSourceFile.value.findIndex(
      item => item.filename === extractedData.filename
    )

    if (existingIndex === -1) {
      extractedSourceFile.value.push({
        filename: extractedData.filename,
        background: extractedData.background || '',
        problem: extractedData.problem || '',
        technical: extractedData.technical || '',
        effect: extractedData.effect || '',
        implementation: extractedData.implementation || '',
        description: extractedData.description || ''
      })
    }
  }

  fetchSaveStep1Cache(projectId.value, getSaveData({ cache: true }))
}

const handleVoiceFileChange = (list) => {
  // 删除了文件
  let cache = list.length < voiceCacheList.value.length

  if (!cache) {
    for(let i = 0; i < list.length; i++) {
      const file = list[i]
      const current = voiceCacheList.value.find(v => v.uid === file.uid)
      if (current && current.progress !== file.progress && file.progress === 100) {
        cache = true
        break
      }
    }
  }
  voiceSliderList.value = [...list]
  voiceCacheList.value = JSON.parse(JSON.stringify(list?.map(v => ({ uid: v.uid, progress: v.uid }))))

  if (cache) {
    fetchSaveStep1Cache(projectId.value, getSaveData({ cache: true }))
  }
}

const handleUploadFileChange = (list) => {
  // 删除了文件
  let cache = list.length < uploadCacheList.value.length

  if (!cache) {
    for(let i = 0; i < list.length; i++) {
      const file = list[i]
      const current = uploadCacheList.value.find(v => v.uid === file.uid)
      if (current && current.progress !== file.progress && file.progress === 100) {
        cache = true
        break
      }
    }
  }
  uploadCacheList.value = JSON.parse(JSON.stringify(list?.map(v => ({ uid: v.uid, progress: v.uid }))))

  if (cache) {
    fetchSaveStep1Cache(projectId.value, getSaveData({ cache: true }))
  }
}

const handleChangeVoiceAnchor = (uid) => {
  voiceInputSectionRef.value.expand(uid)
}

const handleVoiceInput = (extractedData) => {
  handleUpdateFormData(extractedData)
}

const handleFieldModified = (field, isUserModified = false) => {
  // 只有当isUserModified为true时才更新进度状态
  if (isUserModified) {
    progressStatus.value[field] = true
  }
}

const handleWindowResize = () => {
  mainHeight.value = window.innerHeight - 195;
}

const handleShowGuide = (visible) => {
  guideVisible.value = visible
}

const handleScoreUpdate = (val) => {
  scores.value = val
  fetchSaveStep3Cache(projectId.value, getSaveData({ cache: true }))
}

const handleCacheAI = () => {
  fetchSaveStep3Cache(projectId.value, getSaveData({ cache: true }))
}

const handleReasonUpdate = ({key, value}) => {
  formReasonData.value[`step${currentStep.value}`][key] = value
}

const handleReasonStatus = ({ field, status, success }) => {
  if (status && !reasonGeneratingFields.value[field]) {
    reasonTimeStart.value[field] = new Date().getTime()
  }
  if (!status && reasonGeneratingFields.value[field]) {
    reasonTime.value[field] = new Date().getTime() - reasonTimeStart.value[field]
  }
  reasonGeneratingFields.value[field] = status
  reasonExpand.value[field] = status
}

// 点击步骤条
const handleChangeStep = (stepIndex, step) => {
  if (stepIndex < currentStep.value) {
    prevStepDialogRef.value.open(step.title, formData.value[`step${stepIndex}`], stepIndex)
  }
}

watch(currentStep, (newVal) => {
  progressStatus.value = {
    background: false,
    problem: false,
    technical: false,
    effect: false,
    implementation: false,
    termInterpretation: false,
    description: false
  }
  localStorage.setItem('currentStep', newVal)
})

const arraySpanMethod = ({ rowIndex, columnIndex }) => {
  if (rowIndex > 1) {
    if (columnIndex > 2) {
      return [0,0]
    }
    if (columnIndex === 2) {
      return [1,3]
    }
  }
}
const previewTableData = computed(() => {
  const currentData = formData.value.step3;

  const tableData = [
    {
      label: '发明名称',
      content: currentData.title || '暂无',
      label2: '发明人',
      content2: currentData.inventor || '暂无',
    },
    {
      label: '技术领域',
      content: currentData.field || '暂无',
      label2: '项目归属',
      content2: currentData.project || '暂无',
    },
    { label: '背景技术', content: currentData.background },
    { label: '技术问题', content: currentData.problem },
    { label: '技术方案', content: currentData.technical },
    { label: '有益效果', content: currentData.effect },
    { label: '具体实施方式', content: currentData.implementation },
    {
      label: '附图及简要说明',
      content: currentData.description,
      imageUrls: currentData.imageUrls,
      hasImage: !!currentData.imageUrls.length
    }
  ];

  return tableData.flat().filter(item => item.content || item.content2 || item.hasImage);
});

const handleFileExtract = (extractedData) => {
  handleUpdateFormData(extractedData)
}

const handleChangeImage = (images, cache = true) => {
  formData.value[`step${currentStep.value}`].imageUrls = images
  if (cache) {
    fetchSaveStep1Cache(projectId.value, getSaveData({ cache: true }))
  }
}
const handleDirectPreview3 = async (saveFlag = true) => {
  const loading = ElLoading.service({
    lock: true,
    text: '正在生成文档预览...',
    background: 'rgba(255, 255, 255, 0.7)'
  });

  try {
    previewMenuVisible.value = false
    console.log()
    const officeExporter = new OfficeExporter({
      containerSelector: '.preview-office-viewer'
    });
    // 在组件中使用
    await officeExporter.exportAndPreview(formData.value, currentStep.value, projectId.value);
    // await nextTick();
    loading.close();
    saveFlag && ElMessage.success('文档预览已生成')
  } catch (error) {
    loading.close();
    ElMessage.error('文档预览失败: ' + error.message);
    console.error('预览错误:', error);
  }
}
const handleDirectPreview2 = async (saveFlag = true) => {
  const loading = ElLoading.service({
    lock: true,
    text: '正在生成文档预览...',
    background: 'rgba(255, 255, 255, 0.7)'
  });
  try {
    //之前的逻辑
    previewMenuVisible.value = false
    const stepData = formData.value[`step${currentStep.value}`]
    // const formData = formData.value[`step${currentStep.value}`];
    console.log('stepData:',stepData)

    formData.value[`step${currentStep.value + 1}`] = {
      ...stepData,
      imageUrls: stepData.imageUrls
    }
    // 确保当前步骤为预览步骤并等待DOM更新，修改currentstep同时
    if (saveFlag) {
      fetchSaveStep3(projectId.value, getSaveData())
    }
    currentStep.value++
    await nextTick();

    const officeExporter = new OfficeExporter({
      containerSelector: '.preview-office-viewer'
    });
    // 在组件中使用
    await officeExporter.exportAndPreview(formData.value, currentStep.value, projectId.value);
    getRecordList(projectId.value)
    // await nextTick();
    loading.close();
    saveFlag && ElMessage.success('文档预览已生成')
    
  } catch (error) {
    loading.close();
    ElMessage.error('文档预览失败: ' + error.message);
    console.error('预览错误:', error);
  }
}
// 添加新的处理函数
const handleDirectPreview = async (saveFlag = true) => {
  previewMenuVisible.value = false
  const stepData = formData.value[`step${currentStep.value}`]

  formData.value[`step${currentStep.value + 1}`] = {
    ...stepData,
    imageUrls: stepData.imageUrls
  }

  if (saveFlag) {
    fetchSaveStep3(projectId.value, getSaveData())
  }
  currentStep.value++

  await nextTick()
  saveFlag && ElMessage.success('文档预览已生成')
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const handleTemplatePreview = () => {
  previewMenuVisible.value = false
  templateDialogVisible.value = true
}

const handleTemplateSelected = async (template, saveFlag = true) => {
  // TODO: 根据选择的模板处理数据
  const stepData = formData.value[`step${currentStep.value}`]
  formData.value[`step${currentStep.value + 1}`] = {
    ...stepData,
    imageUrls: stepData.imageUrls
  }

  if (saveFlag) {
    fetchSaveStep3(projectId.value, getSaveData({ template }))
  }
  currentStep.value++
  await nextTick()
  saveFlag && ElMessage.success('已应用模板并生成预览')
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

const handleReScore = () => {
  scores.value = {
    overall: {
      dimensions: [],
      scores: [],
      comments: ''
    },
    completeness: {
      dimensions: [],
      scores: [],
      comments: ''
    },
    innovation: {
      dimensions: [],
      scores: [],
      comments: ''
    },
    novelty: {
      dimensions: [],
      scores: [],
      comments: ''
    }
  }
  if (currentStep.value === 1) {
    fetchScores(formData.value[`step${currentStep.value}`])
  } else if (currentStep.value === 2 && TechnicalInfoFormRef.value){
    TechnicalInfoFormRef.value.fetchScores(formData.value[`step${currentStep.value}`])
  }
}
/**
 * 异步获取评分
 * @param {Object} content - 需要评分的内容
 */
const fetchScores = async (content) => {
  try {
    // 如果有正在进行的评分请求，取消它
    if (scoreAbortController.value) {
      scoreAbortController.value.abort()
    }

    // 创建新的 AbortController
    scoreAbortController.value = new AbortController()

    const response = await fetch('/api/score', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ content, step: currentStep.value, step1_content: getStepParams().step1 }),
      signal: scoreAbortController.value.signal
    })

    if (!response.ok) {
      throw new Error('评分请求失败')
    }

    const data = await response.json()

    // 如果请求已经被取消，不更新scores
    if (scoreAbortController.value?.signal.aborted) {
      return
    }

    // 检查返回的数据结构并更新scores
    if (data.code === 0 && data.content) {
      const content = data.content
      // 格式化评论并保持原有数据结构
      scores.value = {
        ...content,
        overall: {
          ...content.overall,
          comments: content.overall.comments.map((comment, index) => `${index + 1}. ${comment}`).join('\n')
        },
        background: {
          ...content.background,
          comments: content.background.comments.map((comment, index) => `${index + 1}. ${comment}`).join('\n')
        },
        solution: {
          ...content.solution,
          comments: content.solution.comments.map((comment, index) => `${index + 1}. ${comment}`).join('\n')
        },
        effect: {
          ...content.effect,
          comments: content.effect.comments.map((comment, index) => `${index + 1}. ${comment}`).join('\n')
        }
      }
      // 评分结束缓存
      if (currentStep.value === 1) {
        fetchSaveStep2Cache(projectId.value, getSaveData({ cache: true }))
      }
    }
  } catch (error) {
    if (error.name === 'AbortError') {
      console.log('评分请求被取消')
      return
    }
    console.error('获取评分失败:', error)
    // 评分失败不影响主流程,只在控制台打印错误
  } finally {
    // 清理 AbortController
    scoreAbortController.value = null
  }
}

const getHasScore = (scoresData) => Boolean(
  scoresData &&
  scoresData?.overall?.dimensions?.length > 0 &&
  scoresData?.overall?.scores?.length > 0 &&
  scoresData?.overall?.max_scores?.length > 0 &&
  scoresData?.background?.dimensions?.length > 0 &&
  scoresData?.background?.scores?.length > 0 &&
  scoresData?.background?.max_scores?.length > 0 &&
  scoresData?.solution?.dimensions?.length > 0 &&
  scoresData?.solution?.scores?.length > 0 &&
  scoresData?.solution?.max_scores?.length > 0 &&
  scoresData?.effect?.dimensions?.length > 0 &&
  scoresData?.effect?.scores?.length > 0 &&
  scoresData?.effect?.max_scores?.length > 0
)

// 修改处理滚动的方法
const handleScrollTo = (element) => {
  if (element) {
    element?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }
}

// 添加处理生成状态的函数
const handleGeneratingStatus = ({ field, status, success }) => {
  generatingFields.value[field] = status
  // 更新整体生成状态
  isGenerating.value = Object.values(generatingFields.value).some(v => v)
  if (!isAuto.value && success) {
    fetchSaveStep3Cache(projectId.value, getSaveData({ cache: true }))
  }
}

// 保存的数据
const getSaveData = (others) => {
  // 通用的处理
  const stepData = formData.value[`step${currentStep.value}`]
  const voiceExtract = {}
  const fileExtract = {}
  voiceInputSectionRef.value?.voiceFiles?.forEach(file => {
    voiceExtract[file.id] = file.text
  })
  uploadFileSectionRef.value?.uploadFiles?.forEach(file => {
    if (file.progress === 100) {
      fileExtract[file.id] = true
    }
  })
  const result = { formData: stepData, scores: scores.value, voiceExtract, fileExtract }

  if (currentStep.value === 0) {
    result.extractedSourceFile = unref(extractedSourceFile.value)
  }

  if (currentStep.value === 2) {
    result.isAuto = unref(isAuto.value)
    const confirmButton = TechnicalInfoFormRef.value?.getConfirmButton()
    console.log('savedata confirmButton', confirmButton)
    result.confirmButton = confirmButton
    result.AIHistory = TechnicalInfoFormRef.value?.getAIHistory()
  }

  result.generateHighlight = unref(generateHighlight.value)

  return { ...result, ...others }
}

const setDatafromCache = () => {
  // 获取详情回显数据
  const savedData = localStorage.getItem('writing-formData')
  if (savedData) {
    formData.value = JSON.parse(savedData)
  }

  const savedStep = localStorage.getItem('currentStep')
  if (savedStep !== null) {
    currentStep.value = parseInt(savedStep)
  }

  const scoresData = localStorage.getItem('writing-scores')
  if (scoresData) {
    scores.value = JSON.parse(scoresData)
  }
}

const handleInit = () => {
  if (projectId.value) {
    fetchInfo(projectId.value).then(res => {
      if (res) {
        const { step1, step2, step3, step4, uploadFiles } = res
        let currentData;
        let scoreStepData = {};

        if (step1) {
          currentStep.value = 0
          currentData = JSON.parse(step1)
          formData.value.step0 = currentData.formData
          extractedSourceFile.value = currentData.extractedSourceFile || []
        }
        if (step2) {
          currentStep.value = 1
          currentData = JSON.parse(step2)
          formData.value.step1 = currentData.formData
          scores.value = currentData.scores
          scoreStepData.step1 = currentData.scores
        }
        if (step3) {
          currentStep.value = 2
          currentData = JSON.parse(step3)
          formData.value.step2 = currentData.formData
          scores.value = currentData.scores
          scoreStepData.step2 = currentData.scores
          isAuto.value = currentData.isAuto
          savedFormData.value = formData.value.step1
        }
        if (step4) {
          // 查看模式
          isView.value = true
          currentStep.value = 3
          currentData = JSON.parse(step4)
          formData.value.step3 = currentData.formData
          scores.value = currentData.scores
          scoreStepData.step3 = currentData.scores
        }

        console.log('currentData', currentData)
        if (uploadFiles?.length) {
          uploadFiles.forEach(item => {
            if (item.fileExt === '1') {
              // 语音文件
              fetch(item.fileUrl).then(res => res.blob()).then(blob => {
                voiceInputSectionRef.value.getAudioDuration(item.fileUrl, (duration) => {
                  const text = currentData?.voiceExtract?.[item.id]

                  defaultFileData.value.voice.push({
                    name: item.fileName,
                    uid: item.id,
                    id: item.id,
                    progress: text ? 100 : 0,
                    duration,
                    text,
                    url: item.fileUrl,
                    file: new File([blob], item.fileName, { type: blob.type }),
                  })
                })
              })
            }
            if (item.fileExt === '3') {
              // word文件
              fetch(item.fileUrl).then(res => res.blob()).then(blob => {
                defaultFileData.value.file.push({
                  name: item.fileName,
                  uid: item.id,
                  id: item.id,
                  progress: currentData?.fileExtract?.[item.id] ? 100 : 0,
                  file: new File([blob], item.fileName, { type: blob.type }),
                  type: item.source
                })
              })
            }
            // 上传的图片文件
            if (item.fileExt === '2' && item.source === '3') {

            }
          })
        }

        // 根据保存内容，自动切换到下一步
        if (currentStep.value === 0 && currentData?.formData && !currentData.cache) {
          handleGenerateOutline(false)
        } else if (currentStep.value === 1 && currentData?.formData && !currentData.cache) {
          const isAuto = currentData.isAuto
          handleDraftOk(isAuto, false)
        } else if (currentStep.value === 2 && currentData?.formData && !currentData.cache) {
          if (currentData.template) {
            handleTemplateSelected(currentData.template, false)
          } else {
            handleDirectPreview(false)
          }
        }

        const hasScore = getHasScore(currentData?.scores)

        if (isView.value) {
          viewScoreData.value = scoreStepData
          currentStep.value = 0
        } else {
          generateHighlight.value = currentData?.generateHighlight || {}

          if (currentData?.cache && currentData.formData && !hasScore) {
            // 缓存状态并且没有评分数据的话，调用评分接口
            currentStep.value === 1 && fetchScores(currentData.formData)
            currentStep.value === 2 && currentData.formData.background && currentData.formData.problem && TechnicalInfoFormRef.value?.fetchScores(currentData.formData)
          }

          if (currentData?.cache && currentStep.value === 2) {
            if (!currentData.isAuto) {
              TechnicalInfoFormRef.value?.setConfirmButton(currentData?.confirmButton || '')
            }
            setTimeout(() => {
              TechnicalInfoFormRef.value?.setAIHistory(currentData.AIHistory)
            }, 1000);
          }
        }

      } else {
        setDatafromCache()
      }
    })
  } else {
    setDatafromCache()
  }
}

const setCacheInterval = () => {
  if (cacheInterval.value) {
    clearInterval(cacheInterval.value)
  }

  cacheInterval.value = setInterval(() => {
    if (formData.value) {
      localStorage.setItem('writing-formData', JSON.stringify(formData.value))
    }
    if (scores.value) {
      localStorage.setItem('writing-scores', JSON.stringify(scores.value))
    }
    if (Object.values(generatingFields.value).every(v => !v)) {
      if (currentStep.value === 0) {
        fetchSaveStep1Cache(projectId.value, getSaveData({ cache: true }))
      }
      if (currentStep.value === 1 && !isGenerateButtonDisabled.value) {
        fetchSaveStep2Cache(projectId.value, getSaveData({ cache: true }))
      }
      if (currentStep.value === 2) {
        fetchSaveStep3Cache(projectId.value, getSaveData({ cache: true }))
      }
    }
  }, cacheIntervalTime);
}

const handleSaveModel = () => {
  fetchSaveStepModel(projectId.value, currentStep.value + 1, getSaveData())
}

onMounted(() => {
  try {
    handleInit()
    setCacheInterval()
    handleWindowResize()
    window.addEventListener('resize', handleWindowResize)
    guideEmitter.on('show', handleShowGuide)
    getRecordList(projectId.value)
  } catch (error) {
    console.error('恢复状态失败:', error)
  }
})


onUnmounted(() => {
  window.removeEventListener('resize', handleWindowResize)
  guideEmitter.off('show', handleShowGuide)
  if (cacheInterval.value) {
    clearInterval(cacheInterval.value)
  }
  // 取消所有正在进行的请求
  if (abortController.value) {
    isAborted.value = true
    abortController.value.abort()
  }
  if (TechnicalInfoFormRef.value) {
    TechnicalInfoFormRef.value.stopScore()
  }
})

watch(() => route.query.id, (newVal) => {
  if (newVal) {
    projectId.value = newVal
    handleInit()
  }
})
</script>

<style scoped lang="scss">
// .section[page-break-befor] :always
.writing-container {
  width: 100%;
  /* min-height: 100vh; */
  :deep(.el-scrollbar__view) {
    position: relative;
    height: 100%;
    overflow: auto;
  }
}

.main-content {
  display: flex;
  width: 100%;
  height: calc(100vh - 195px);
}

.left-panel,
.right-panel {
  position: relative;
  height: fit-content;
  max-height: 100%;
  box-sizing: border-box;
  :deep(.el-scrollbar__view) {
    padding: 0 20px 40px;
  }
}

.left-panel {
  width: 17.5%;
  height: 100%;
  &.left-panel-slider {
    width: calc(17.5% + 207px);
    .data-source {
      .slider-trigger {
        left: 192px;
        transform: translateY(-50%)
      }
    }
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 1px;
    height: 100%;
    background:#E1E1E1;
  }
  :deep(.card-title) {
    padding-left: 20px;
    padding-bottom: 10px;
    margin-bottom: 0;
    border-bottom: 1px solid #E1E1E1;
  }
  :deep(.el-scrollbar__view) {
    height: calc(100% - 42px);
  }
  :deep(.el-tabs) {
    flex: 1;
    width: 0;
    .el-tabs__header {
      padding: 14px 20px 18px;
      margin-bottom: 0;
    }
    .el-tabs__nav-wrap {
      padding: 2px;
      background: #F3F3F7;
      border-radius: 6px;
      box-sizing: border-box;
      &::after {
        display: none;
      }
    }
    .el-tabs__nav {
      width: 100%;
      .el-tabs__active-bar {
        height: 32px;
        top: 2px;
        background: #fff;
        border-radius: 4px;
      }
      .el-tabs__item {
        flex: 1;
        height: 36px;
        padding: 0;
        color: var(--el-text-color-regular);
        z-index: 10;
      }
    }
    &::after {
      content: none;
    }
  }
  .data-source {
    height: calc(100% - 41px);
    position: relative;
    display: flex;
    .data-source-slider {
      width: 0;
      position: relative;
      border-right: 1px solid #E1E1E1;
      transition: all .5s;
      overflow: hidden;
      &.open {
        width: 207px;
      }
    }
    .slider-trigger {
      position: absolute;
      top: 50%;
      left: 0;
      z-index: 100;
      transform: translateY(-50%) rotate(180deg);
      transition: left .5s;
      cursor: pointer;
      img {
        height: auto;
      }
    }
    :deep(.el-tab-pane) {
      height: 100%;
    }
  }
}

.main-panel {
  flex: 1;
  :deep(.form-section) {
    padding: 0 6.6%;
  }
}

.right-panel {
  width: 20%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  :deep(.card-title) {
    margin-bottom: 3px;
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 100%;
    background:#E1E1E1;
  }
}

.preview-section {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: white;
  position: relative;

  .preview-table {
    position: relative;
    z-index: 200;
    margin-bottom: 20px;
    overflow: auto;
    border: 1px solid #999;

    :deep(.el-table) {
      // 隐藏表头
      .el-table__header-wrapper {
        display: none;
      }

      .el-table__inner-wrapper,
      &.el-table--border {
        &::after,
        &::before {
          content: none;
        }
      }
      .el-table__border-left-patch {
        display: none;
      }

      // 优化单元格样式
      .el-table__row {
        td {
          color: #555;
          vertical-align: middle;
          border-color: #999;

          &:nth-child(2), &:nth-child(4) {
            color: #000;
            text-align: center;
            font-weight: 600;
          }
          &:last-child {
            border-right: 0;
          }
        }

        &:last-child td {
          border-bottom: 0;
        }
        // hover效果
        &:hover td {
          background-color: rgba(0,161,96,0.1);
        }
      }

      // 文本样式化
      .cell {
        padding: 0;
        white-space: pre-wrap;
        line-height: 1.8;
        font-size: 14px;
      }
    }
    .content-cell {
      padding: 16px;

      .content-wrapper {
        display: flex;
        flex-direction: column;
        gap: 24px;
        align-items: flex-start;
      }

      .text-content {
        flex: 1;
        white-space: pre-wrap;
        line-height: 1.8;
        color: #333;
        font-size: 14px;
      }

      .image-container {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 12px;
      }

      .image-title {
        font-weight: bold;
      }

      .preview-image {
        width: 140px;
        height: 120px;
        border-radius: 6px;
        overflow: hidden;
        background: #fff;
        border: 1px solid #e4e7ed;
        transition: all 0.3s ease;

        &:hover {
          border-color: var(--el-color-primary);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
      }

      .image-placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        color: #909399;
        font-size: 24px;
      }
    }
  }
}

.button-group {
  display: flex;
  gap: 12px;
  height: 60px;
  justify-content: center;
  align-items: center;
  border-top: 1px solid #E1E1E1;
  :deep(.el-button) {
    min-width: 160px;
  }
}

.generate-btn {
  width: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 24px;
  color: white;

  // 添加禁用状态的样式
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.generate-icon {
  font-size: 16px;
}

@media (max-width: 1366px) {
  .left-panel {
    width: 23%;
    &.left-panel-slider {
      width: calc(23% + 207px)
    }
  }
  .main-panel {
    .form-section {
      padding: 0 4%;
    }
  }
  .right-panel {
    width: 22%
  }
}

.preview-menu {
  display: flex;
  flex-direction: column;

  .menu-button {
    width: 100%;
    padding: 16px 20px;
    justify-content: flex-start;
    font-size: 15px;
    transition: all 0.3s ease;

    :deep(.el-icon) {
      margin-right: 8px;
      font-size: 18px;
    }

    &:hover {
      background-color: var(--el-fill-color-light);
      color: var(--el-color-primary);
    }
  }

  .divider {
    height: 1px;
    background: var(--el-border-color-lighter);
    margin: 4px 0;
  }
}

:deep(.preview-menu-popover) {
  padding: 0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  .el-popper__arrow::before {
    background: white;
    border-color: var(--el-border-color-lighter) !important;
  }
}

.preview-image {
  max-width: 300px;
  max-height: 200px;
  margin-top: 10px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-item {
  scroll-margin-top: 100px; // 添加滚动偏移量
  // ... existing styles ...
}

</style>

<style lang="scss">
.guide-tour {
  color: #fff;
  .el-tour__content {
    max-width: 440px;
    padding: 24px 30px;
    background: var(--el-color-primary);
    box-sizing: border-box;
    .el-tour__title {
      margin-bottom: 20px;
      color: #fff;
      font-size: 18px;
    }
    .el-tour__body {
      font-size: 16px;
      line-height: 20px;
    }
    .el-tour__arrow {
      background: var(--el-color-primary);
    }
    .el-tour__footer {
      flex-wrap: wrap;
      gap: 10px;
    }
    .el-tour-indicators {
      display: flex;
      justify-content: space-between;
      align-items: center;
      > a {
        cursor: pointer;
        &:hover {
          color: var(--el-color-primary-light-7);
        }
      }
    }
    .el-tour-buttons {
      .el-button {
        min-width: 100px;
        height: 36px;
        line-height: 36px;
        color: #fff;
        background: rgba(255,255,255,0.21);
        border-radius: 6px;
        border: none;
      }
    }
  }
}
</style>
